import { UAParser } from 'ua-parser-js';
import { sendDeviceMetadata } from '../services/deviceMetadata.services';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'device_metadata';
const SESSION_ID_KEY = 'session_id';

class DeviceMetadataManager {
  constructor() {
    this.currentMetadata = null;
    this.sessionId = this.getOrCreateSessionId();
    this.isProcessing = false;
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  async getPublicIP() {
    try {
      const response = await fetch('https://api.ipify.org/?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Failed to get public IP:', error);
      return null;
    }
  }

  getDeviceInfo() {
    const parser = new UAParser();
    const result = parser.getResult();
    
    return {
      device_type: result.device.type || 'desktop',
      operating_system: `${result.os.name || 'Unknown'} ${result.os.version || ''}`.trim(),
      browser: result.browser.name || 'Unknown',
      browser_version: result.browser.version || 'Unknown',
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      timestamp: new Date().toISOString()
    };
  }

  getUserContext() {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('accessToken');
    
    return {
      user_id: userId || null,
      token: token || null,
      session_id: this.sessionId
    };
  }

  async collectMetadata() {
    const deviceInfo = this.getDeviceInfo();
    const publicIP = await this.getPublicIP();
    
    return {
      device_metadata: {
        ...deviceInfo,
        public_ip: publicIP
      },
      user_context: this.getUserContext()
    };
  }

  getStoredMetadata() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to parse stored metadata:', error);
      return null;
    }
  }

  storeMetadata(metadata) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(metadata));
    } catch (error) {
      console.error('Failed to store metadata:', error);
    }
  }

  hasMetadataChanged(newMetadata, storedMetadata) {
    if (!storedMetadata) return true;

    // Compare device metadata excluding timestamp
    const { timestamp: newTimestamp, ...newDevice } = newMetadata.device_metadata;
    const { timestamp: storedTimestamp, ...storedDevice } = storedMetadata.device_metadata;
    const deviceChanged = JSON.stringify(newDevice) !== JSON.stringify(storedDevice);
    
    // Check if user context changed (login/logout)
    const userChanged = JSON.stringify(newMetadata.user_context) !== 
                       JSON.stringify(storedMetadata.user_context);

    return deviceChanged || userChanged;
  }

  async sendToAPI(metadata) {
    try {
      await sendDeviceMetadata(metadata);
      console.info('Device metadata sent successfully');
    } catch (error) {
      console.error('Failed to send device metadata:', error);
    }
  }

  async processMetadata() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      const newMetadata = await this.collectMetadata();
      const storedMetadata = this.getStoredMetadata();

      if (this.hasMetadataChanged(newMetadata, storedMetadata)) {
        this.storeMetadata(newMetadata);
        await this.sendToAPI(newMetadata);
        this.currentMetadata = newMetadata;
      }
    } finally {
      this.isProcessing = false;
    }
  }

  // Public method to trigger metadata collection
  async initialize() {
    await this.processMetadata();
  }

  // Public method to update when user context changes
  async updateUserContext() {
    await this.processMetadata();
  }
}

// Create singleton instance
const deviceMetadataManager = new DeviceMetadataManager();

export default deviceMetadataManager;